import React from 'react';
import { common } from '@kurtosys/ksys-app-template';
import { observer } from 'mobx-react';

import { ComponentBase, InjectedStyledComponent } from '../../../../../shared/index.js';
import { TranslationContext } from '../../../Translate/Translate.js';
import Table from '../../styledComponents/DatePickerMonth/Table.js';
import TableBody from '../../styledComponents/DatePickerMonth/TableBody.js';
import TableCell from '../../styledComponents/DatePickerMonth/TableCell.js';
import TableHead from '../../styledComponents/DatePickerMonth/TableHead.js';
import TableHeadCell from '../../styledComponents/DatePickerMonth/TableHeadCell.js';
import TableRow from '../../styledComponents/DatePickerMonth/TableRow.js';
import DatePickerDay from '../DatePickerDay/DatePickerDay.js';

import { IDatePickerMonthConfiguration, IDatePickerMonthProps, IDatePickerMonthState } from './models/index.js';
import {
	DATE_PICKER_MONTH_CATEGORY_KEY,
	DATE_PICKER_MONTH_COMPONENT_KEY,
	DATE_PICKER_MONTH_PARENT_COMPONENT_KEY,
} from './DatePickerMonth.meta.js';

function chunk<T>(array: T[], chunkSize: number): T[][] {
	const result = [];

	for (let i = 0; i < array.length; i += chunkSize) {
		result.push(array.slice(i, i + chunkSize));
	}

	return result;
}

function mapWithOffset<T, U>(array: T[], startingOffset: number, mapFn: (item: T) => U): U[] {
	return array.map((_, i) => {
		const adjustedIndex = (i + startingOffset) % array.length;
		return mapFn(array[adjustedIndex]);
	});
}

export class DatePickerMonth extends ComponentBase<
	IDatePickerMonthProps,
	IDatePickerMonthState,
	IDatePickerMonthConfiguration
> {
	categoryKey = DATE_PICKER_MONTH_CATEGORY_KEY;
	componentKey = DATE_PICKER_MONTH_COMPONENT_KEY;
	get parentComponentKey(): string | undefined {
		return DATE_PICKER_MONTH_PARENT_COMPONENT_KEY;
	}

	constructor(props: IDatePickerMonthProps) {
		super(props);
	}
	render() {
		const {
			selectedDate,
			focusedDate,
			labelledById,
			localization,
			firstDayOfWeek,
			min,
			max,
			dateFormatter,
			isDateDisabled,
			onDateSelect,
			onKeyboardNavigation,
			focusedDayRef,
		} = this.props;

		const today = new Date();
		const days = common.commonUtils.getViewOfMonth(focusedDate, firstDayOfWeek);

		return (
			<TranslationContext.Consumer>
				{(translationContext) => {
					const { translate } = translationContext;
					return (
						<Table className="table" aria-labelledby={labelledById}>
							<TableHead>
								<TableRow>
									{mapWithOffset(localization.dayNames, firstDayOfWeek, (dayName: string) => {
										const translatedDayName = translate(dayName);
										return (
											<TableHeadCell key={translatedDayName} className="table-header" scope="col">
												<span aria-hidden="true">{translatedDayName.substring(0, 2)}</span>
												<span className="vhidden">{translatedDayName}</span>
											</TableHeadCell>
										);
									})}
								</TableRow>
							</TableHead>
							<TableBody>
								{chunk(days, 7).map((week: Date[], index) => (
									<TableRow key={`week-${index}`} className="row">
										{week.map((day) => (
											<TableCell key={`week-${index}${day}`} className="cell">
												<DatePickerDay
													day={day}
													today={today}
													focusedDay={focusedDate}
													isSelected={common.commonUtils.isEqual(day, selectedDate)}
													disabled={isDateDisabled(day)}
													inRange={common.commonUtils.inRange(day, min, max)}
													onDaySelect={onDateSelect}
													dateFormatter={dateFormatter}
													onKeyboardNavigation={onKeyboardNavigation}
													focusedDayRef={focusedDayRef}
												/>
											</TableCell>
										))}
									</TableRow>
								))}
							</TableBody>
						</Table>
					);
				}}
			</TranslationContext.Consumer>
		);
	}
}

export default InjectedStyledComponent(
	observer(DatePickerMonth),
	DATE_PICKER_MONTH_CATEGORY_KEY,
	DATE_PICKER_MONTH_PARENT_COMPONENT_KEY,
	[DATE_PICKER_MONTH_COMPONENT_KEY],
);
